import React from "react";
import {
  FiClock,
  FiX,
  FiCheck,
  FiEdit,
  FiMail,
  FiPhone,
  FiAlertTriangle,
} from "react-icons/fi";
import { Appointment } from "../../types";
import utils from "../../helpers/utils";
import Strings from "../../constants/Strings";

interface AppointmentItemProps {
  appointment: Appointment;
  allAppointments?: Appointment[];
  onEdit?: (appointment: Appointment) => void;
  onConfirm?: (appointmentId: number) => void;
  onCancel?: (appointmentId: number) => void;
  showActions?: boolean;
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case "confirmed":
      return {
        bg: "bg-green-100 dark:bg-green-900/30",
        text: "text-green-700 dark:text-green-400",
        ring: "ring-green-600/20 dark:ring-green-400/20",
        label: "Confirmado",
      };
    case "cancelled":
      return {
        bg: "bg-red-100 dark:bg-red-900/30",
        text: "text-red-700 dark:text-red-400",
        ring: "ring-red-600/20 dark:ring-red-400/20",
        label: "Cancelado",
      };
    default:
      return {
        bg: "bg-yellow-100 dark:bg-yellow-900/30",
        text: "text-yellow-700 dark:text-yellow-400",
        ring: "ring-yellow-600/20 dark:ring-yellow-400/20",
        label: "Pendente",
      };
  }
};

const hasConflict = (
  appointment: Appointment,
  allAppointments: Appointment[]
): boolean => {
  // Não verificar conflitos para compromissos cancelados
  if (appointment.status === "cancelled") {
    return false;
  }

  // Converter para timezone de São Paulo antes de comparar
  const aptStart = utils.toSaoPauloTimeZone(appointment.start_time).getTime();
  const aptEnd = utils.toSaoPauloTimeZone(appointment.end_time).getTime();

  // Verificar se há conflito com outros compromissos (exceto cancelados)
  return allAppointments.some((other) => {
    // Não comparar com o mesmo compromisso
    if (other.id === appointment.id) {
      return false;
    }

    // Ignorar compromissos cancelados
    if (other.status === "cancelled") {
      return false;
    }

    const otherStart = utils.toSaoPauloTimeZone(other.start_time).getTime();
    const otherEnd = utils.toSaoPauloTimeZone(other.end_time).getTime();

    // Verificar sobreposição de horários
    // Conflito ocorre quando os intervalos se sobrepõem
    return aptStart < otherEnd && aptEnd > otherStart;
  });
};

export const AppointmentItem: React.FC<AppointmentItemProps> = ({
  appointment: apt,
  allAppointments = [],
  onEdit,
  onConfirm,
  onCancel,
  showActions = true,
}) => {
  const statusInfo = getStatusBadge(apt.status || "scheduled");
  const hasConflictWarning = hasConflict(apt, allAppointments);

  return (
    <div className="group relative overflow-hidden rounded-lg border border-stroke bg-white p-3.5 shadow-sm transition-all active:scale-[0.98] hover:shadow-md dark:border-strokedark dark:bg-boxdark sm:rounded-xl sm:p-3.5 md:p-5">
      <div className="relative flex flex-col gap-3.5 min-w-0 sm:gap-3">
        <div className="flex items-start justify-between gap-3 min-w-0 sm:gap-3 md:gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1 sm:gap-3 md:gap-4">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="absolute -inset-1 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 blur-sm transition-opacity group-hover:opacity-75 sm:rounded-xl"></div>
              <div className="relative h-14 w-14 overflow-hidden rounded-lg border-2 border-white shadow-md dark:border-boxdark sm:h-13 sm:w-13 md:h-14 md:w-14 sm:rounded-xl">
                <img
                  src={utils.getUriImage(
                    apt.user?.photo && apt.user.photo !== ""
                      ? apt.user.photo
                      : Strings.default_photo
                  )}
                  alt={apt.user?.name || "Aluno"}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex flex-col gap-2 min-w-0 flex-1 sm:gap-2">
              <h4 className="text-base font-bold text-gray-900 dark:text-white truncate sm:text-base md:text-lg">
                {apt.user?.name || "Sem nome"}
              </h4>
              {(apt.user?.email || apt.user?.phone) && (
                <div className="flex flex-col gap-1.5 sm:gap-1.5">
                  {apt.user?.email && (
                    <div className="flex items-center gap-2 sm:gap-2">
                      <FiMail className="text-gray-400 flex-shrink-0" size={14} />
                      <span className="text-xs text-gray-600 dark:text-gray-400 truncate sm:text-sm">
                        {apt.user.email}
                      </span>
                    </div>
                  )}
                  {apt.user?.phone && (
                    <div className="flex items-center gap-2 sm:gap-2">
                      <FiPhone className="text-gray-400 flex-shrink-0" size={14} />
                      <span className="text-xs text-gray-600 dark:text-gray-400 truncate sm:text-sm">
                        {utils.formatarTelefone(apt.user.phone)}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/50 sm:gap-2 sm:px-3 sm:py-2">
                <FiClock className="text-gray-400 flex-shrink-0" size={15} />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 sm:text-sm">
                  {utils.formatTime(utils.toSaoPauloTimeZone(apt.start_time))} -{" "}
                  {utils.formatTime(utils.toSaoPauloTimeZone(apt.end_time))}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap sm:gap-2">
                <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1.5 text-xs font-semibold text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 whitespace-nowrap sm:px-3 sm:py-1">
                  {apt.type || "Aula"}
                </span>
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset whitespace-nowrap ${statusInfo.bg} ${statusInfo.text} ${statusInfo.ring} sm:px-3 sm:py-1`}
                >
                  {statusInfo.label}
                </span>
                {hasConflictWarning && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-3 py-1.5 text-xs font-semibold text-orange-700 ring-1 ring-inset ring-orange-600/20 dark:bg-orange-900/30 dark:text-orange-400 dark:ring-orange-400/20 whitespace-nowrap sm:px-3 sm:py-1"
                    title="Conflito de horário com outro compromisso"
                  >
                    <FiAlertTriangle size={12} />
                    Conflito
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions - Abaixo no mobile (largura total), ao lado no desktop */}
        {showActions && (
          <div className="flex w-full flex-col gap-2.5 border-t border-stroke pt-3 dark:border-strokedark sm:w-auto sm:flex-row sm:justify-end sm:gap-2 sm:border-0 sm:pt-0 sm:absolute sm:right-0 sm:top-0">
            {apt.status === "cancelled" ? (
              // Se cancelado: mostrar Editar e Confirmar
              <>
                {onEdit && (
                  <button
                    onClick={() => onEdit(apt)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-blue-200 px-4 py-2.5 text-sm font-semibold text-blue-600 transition-all active:scale-95 hover:bg-blue-50 hover:border-blue-300 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20 sm:w-auto sm:rounded-xl sm:px-3 sm:py-2"
                    aria-label="Editar compromisso"
                  >
                    <FiEdit size={18} />
                    <span className="sm:hidden">Editar Compromisso</span>
                  </button>
                )}
                {onConfirm && (
                  <button
                    onClick={() => onConfirm(Number(apt.id))}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-green-200 px-4 py-2.5 text-sm font-semibold text-green-600 transition-all active:scale-95 hover:bg-green-50 hover:border-green-300 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-900/20 sm:w-auto sm:rounded-xl sm:px-3 sm:py-2"
                    aria-label="Confirmar compromisso"
                  >
                    <FiCheck size={18} />
                    <span className="sm:hidden">Confirmar Compromisso</span>
                  </button>
                )}
              </>
            ) : apt.status === "confirmed" ? (
              // Se confirmado: mostrar Cancelar e Editar
              <>
                {onEdit && (
                  <button
                    onClick={() => onEdit(apt)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-blue-200 px-4 py-2.5 text-sm font-semibold text-blue-600 transition-all active:scale-95 hover:bg-blue-50 hover:border-blue-300 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20 sm:w-auto sm:rounded-xl sm:px-3 sm:py-2"
                    aria-label="Editar compromisso"
                  >
                    <FiEdit size={18} />
                    <span className="sm:hidden">Editar Compromisso</span>
                  </button>
                )}
                {onCancel && (
                  <button
                    onClick={() => onCancel(Number(apt.id))}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 transition-all active:scale-95 hover:bg-red-50 hover:border-red-300 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 sm:w-auto sm:rounded-xl sm:px-3 sm:py-2"
                    aria-label="Cancelar compromisso"
                  >
                    <FiX size={18} />
                    <span className="sm:hidden">Cancelar Compromisso</span>
                  </button>
                )}
              </>
            ) : (
              // Se pendente: mostrar Editar, Confirmar e Cancelar
              <>
                {onEdit && (
                  <button
                    onClick={() => onEdit(apt)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-blue-200 px-4 py-2.5 text-sm font-semibold text-blue-600 transition-all active:scale-95 hover:bg-blue-50 hover:border-blue-300 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20 sm:w-auto sm:rounded-xl sm:px-3 sm:py-2"
                    aria-label="Editar compromisso"
                  >
                    <FiEdit size={18} />
                    <span className="sm:hidden">Editar Compromisso</span>
                  </button>
                )}
                {onConfirm && (
                  <button
                    onClick={() => onConfirm(Number(apt.id))}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-green-200 px-4 py-2.5 text-sm font-semibold text-green-600 transition-all active:scale-95 hover:bg-green-50 hover:border-green-300 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-900/20 sm:w-auto sm:rounded-xl sm:px-3 sm:py-2"
                    aria-label="Confirmar compromisso"
                  >
                    <FiCheck size={18} />
                    <span className="sm:hidden">Confirmar Compromisso</span>
                  </button>
                )}
                {onCancel && (
                  <button
                    onClick={() => onCancel(Number(apt.id))}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 transition-all active:scale-95 hover:bg-red-50 hover:border-red-300 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 sm:w-auto sm:rounded-xl sm:px-3 sm:py-2"
                    aria-label="Cancelar compromisso"
                  >
                    <FiX size={18} />
                    <span className="sm:hidden">Cancelar Compromisso</span>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
